/**
 * "Stories owned": the Stories and chapters this user owns (bought, granted or gifted), shown under the
 * subscription because together they decide what the user can play.
 *
 * Moderators see the list read-only; granting and revoking are Admin-only (the Admin API refuses them with 403
 * too). Both writes are idempotent on the server, so a double click or a retry returns the same row.
 */

import { useEffect, useMemo, useState } from 'react'
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from '@mui/material'
import { AutoStories, Block, Add } from '@mui/icons-material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { DataTable, type DataTableColumn } from '@components/common/DataTable'
import { storiesApi } from '@api/endpoints/stories'
import type { ContentEntitlementDto, EntitlementStoryOptionDto } from '@api/types/stories'
import { UserDataActionDialog } from './UserDataActionDialog'
import { adminUserEntitlementsKey } from './userQueryKeys'
import { useIsAdmin } from './useIsAdmin'
import { useTablePaging } from './useTablePaging'
import { apiErrorMessage, errorMessage, formatDay } from './userFormatting'

export function UserEntitlementsPanel({ userId }: { userId: string }) {
  const isAdmin = useIsAdmin()
  const paging = useTablePaging()
  const [granting, setGranting] = useState(false)
  const [revoking, setRevoking] = useState<ContentEntitlementDto | null>(null)

  const { data, isLoading, error } = useQuery({
    queryKey: adminUserEntitlementsKey(userId),
    queryFn: () => storiesApi.getUserEntitlements(userId),
  })

  const columns: DataTableColumn<ContentEntitlementDto>[] = [
    {
      key: 'story',
      header: 'Story',
      primary: true,
      render: (e) => (
        <Box sx={{ wordBreak: 'break-word' }}>
          <Typography variant="body2" component="span">
            {e.storyName}
          </Typography>
          <Typography variant="caption" color="text.secondary" component="div">
            {e.achievementId ? `Chapter: ${e.chapterName ?? 'unknown'}` : 'Whole story'}
          </Typography>
        </Box>
      ),
    },
    { key: 'source', header: 'Source', render: (e) => <Chip size="small" variant="outlined" label={e.source} /> },
    { key: 'granted', header: 'Granted', render: (e) => formatDay(e.createdAt) },
    {
      key: 'status',
      header: 'Status',
      render: (e) =>
        e.isLive ? (
          <Chip size="small" color="success" variant="outlined" label="Owned" />
        ) : (
          <Box sx={{ wordBreak: 'break-word' }}>
            <Chip size="small" variant="outlined" label={e.revokedAt ? `Revoked ${formatDay(e.revokedAt)}` : 'Revoked'} />
            {e.revokeReason && (
              <Typography variant="caption" color="text.secondary" component="div">
                {e.revokeReason}
              </Typography>
            )}
          </Box>
        ),
    },
  ]

  return (
    <Paper sx={{ p: { xs: 2, sm: 3 }, mt: 2 }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: 'space-between',
          gap: 1.5,
          mb: 2,
        }}
      >
        <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AutoStories fontSize="small" /> Stories owned
        </Typography>
        {isAdmin && (
          <Button variant="outlined" size="small" startIcon={<Add />} onClick={() => setGranting(true)}>
            Grant a story
          </Button>
        )}
      </Box>

      <DataTable
        rows={data ?? []}
        columns={columns}
        getRowKey={(e) => e.id}
        loading={isLoading}
        error={error ? errorMessage(error, 'Failed to load stories owned') : null}
        emptyMessage="This user owns no stories or chapters"
        dense
        {...paging}
        getRowActions={(e) =>
          isAdmin && e.isLive
            ? [{ key: 'revoke', label: 'Revoke', icon: <Block fontSize="small" />, color: 'error', onClick: () => setRevoking(e) }]
            : []
        }
      />

      {isAdmin && granting && <GrantEntitlementDialog userId={userId} onClose={() => setGranting(false)} />}
      {isAdmin && revoking && (
        <RevokeEntitlementDialog userId={userId} entitlement={revoking} onClose={() => setRevoking(null)} />
      )}
    </Paper>
  )
}

/** Wait this long after the last keystroke before searching Stories. */
const SEARCH_DEBOUNCE_MS = 300

function GrantEntitlementDialog({ userId, onClose }: { userId: string; onClose: () => void }) {
  const queryClient = useQueryClient()
  const [input, setInput] = useState('')
  const [term, setTerm] = useState('')
  const [story, setStory] = useState<EntitlementStoryOptionDto | null>(null)
  /** '' = the whole Story. */
  const [chapterId, setChapterId] = useState('')

  useEffect(() => {
    const handle = setTimeout(() => setTerm(input.trim()), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(handle)
  }, [input])

  const search = useQuery({
    queryKey: ['admin-entitlement-stories', term],
    queryFn: () => storiesApi.searchEntitlementStories(term),
    staleTime: 30_000,
  })

  // Keep the chosen Story in the options so the Autocomplete does not drop it when the search moves on.
  const options = useMemo(() => {
    const found = search.data ?? []
    return story && !found.some((s) => s.id === story.id) ? [story, ...found] : found
  }, [search.data, story])

  const mutation = useMutation({
    mutationFn: (reason: string) =>
      storiesApi.grantEntitlement(userId, {
        collectionId: story!.id,
        achievementId: chapterId || null,
        reason: reason || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminUserEntitlementsKey(userId) })
      onClose()
    },
  })

  return (
    <UserDataActionDialog
      title="Grant a story"
      message="The user owns it right away and can play it whatever their plan. Granting something they already own changes nothing."
      confirmLabel="Grant"
      reasonOptional
      validate={() => (story ? null : 'Choose a story.')}
      pending={mutation.isPending}
      error={mutation.error ? apiErrorMessage(mutation.error, 'The story could not be granted.') : null}
      onSubmit={(reason) => mutation.mutate(reason)}
      onClose={onClose}
    >
      <Autocomplete
        sx={{ mt: 2 }}
        options={options}
        value={story}
        onChange={(_, value) => {
          setStory(value)
          setChapterId('')
        }}
        inputValue={input}
        onInputChange={(_, value) => setInput(value)}
        // The server already filtered on the text.
        filterOptions={(x) => x}
        getOptionLabel={(s) => s.name}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        loading={search.isFetching}
        noOptionsText={search.error ? apiErrorMessage(search.error, 'Search failed') : 'No stories match'}
        renderOption={(props, s) => (
          <li {...props} key={s.id}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', minWidth: 0 }}>
              <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                {s.name}
              </Typography>
              {!s.isPublic && <Chip size="small" variant="outlined" label="Hidden" />}
            </Box>
          </li>
        )}
        renderInput={(params) => <TextField {...params} label="Story" autoFocus />}
      />
      <TextField
        select
        fullWidth
        label="What to grant"
        value={chapterId}
        onChange={(e) => setChapterId(e.target.value)}
        disabled={!story}
        sx={{ mt: 2 }}
      >
        <MenuItem value="">Whole story</MenuItem>
        {(story?.chapters ?? []).map((c) => (
          <MenuItem key={c.id} value={c.id}>
            Chapter: {c.name}
          </MenuItem>
        ))}
      </TextField>
    </UserDataActionDialog>
  )
}

function RevokeEntitlementDialog({
  userId,
  entitlement,
  onClose,
}: {
  userId: string
  entitlement: ContentEntitlementDto
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: (reason: string) => storiesApi.revokeEntitlement(userId, entitlement.id, { reason: reason || undefined }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminUserEntitlementsKey(userId) })
      onClose()
    },
  })

  const what = entitlement.achievementId
    ? `the chapter "${entitlement.chapterName ?? 'unknown'}" of ${entitlement.storyName}`
    : entitlement.storyName

  return (
    <UserDataActionDialog
      title="Revoke ownership?"
      message={`The user no longer owns ${what}. The row stays in this list as revoked.`}
      confirmLabel="Revoke"
      destructive
      reasonOptional
      pending={mutation.isPending}
      error={mutation.error ? apiErrorMessage(mutation.error, 'It could not be revoked.') : null}
      onSubmit={(reason) => mutation.mutate(reason)}
      onClose={onClose}
    />
  )
}
